/*
 * Description:
 * ---------------------
 * The Firewall screen's "Across sites": this box's watched device groups
 * beside the same groups at the customer's other sites, from notabox-server's
 * comparison of what each site reports (server/pkg/fleetwatch). A place this
 * site's group reaches that the group at no other site does is what the odd
 * place alert is about. Wording in firewallWords.js.
 *
 * ! file components/FirewallAcross.js
 */
import React, { useCallback, useState } from "react";
import { FiArrowRight } from "react-icons/fi";
import { TbAlertTriangle, TbBuildingStore } from "react-icons/tb";
import { ASSISTANT_NAME } from "../appView";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { IS_SINGLE_EDGE, serverApiUrl } from "../serverApi";
import { askAda } from "../utils/askAda";
import { AiIcon } from "./AiIcon";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import * as W from "./firewallWords";

/* useFleetWatch reads the server's comparison, which only the portal has.
   available is false when the server answers that it has no such route, and
   null until it has answered at all. */
export function useFleetWatch(selectedNode) {
  const [fleet, setFleet] = useState(null);
  const [available, setAvailable] = useState(null);
  const read = useCallback(async () => {
    const res = await fetchWithTimeout(serverApiUrl("/firewall/fleet"), { credentials: "same-origin" }).catch(() => null);
    if (res?.status === 404) setAvailable(false);
    if (!res?.ok) return;
    setAvailable(true);
    setFleet(await res.json().catch(() => null));
  }, []);
  useVisibleInterval(read, 60000, !IS_SINGLE_EDGE && !!selectedNode);
  const customer = (fleet?.customers || []).find((c) => c.groups.some((g) => g.sites.includes(selectedNode)));
  return {
    available,
    groups: customer ? customer.groups.filter((g) => g.sites.includes(selectedNode)) : [],
    odd: customer ? customer.odd.filter((o) => o.node === selectedNode) : [],
    minPeers: fleet?.min_peers,
  };
}

/* Four shops, one of them reaching a place the others do not: what this
   section looks for, drawn. */
function Sketch() {
  return (
    <div className="fw-sketch" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={`fw-sketch-site${i === 2 ? " is-odd" : ""}`}>
          <TbBuildingStore size={26} strokeWidth={1.6} />
          <span className="fw-sketch-dots"><i /><i /><i />{i === 2 && <i className="is-odd" />}</span>
        </div>
      ))}
    </div>
  );
}

/* One group across the customer's sites: the finding first, said plainly,
   then a row per site and a column per place its devices used, a dot where
   that site's group went. Sites run down the page, so 25 shops scroll rather
   than squeeze. A place only this site reaches is lit. */
function Matrix({ group, odd, minPeers, here, go }) {
  const head = W.acrossWords(group, odd, minPeers);
  const lit = new Set(odd.map((o) => o.place));
  const places = group.places || [];
  const sites = [here, ...group.sites.filter((s) => s !== here)].filter((s) => group.sites.includes(s));
  const ask = (o) => askAda(here, W.findingQuestion(group.name, o));
  return (
    <div className={`fw-matrix is-${head.tone || "plain"}`}>
      {odd.map((o) => (
        <div key={o.place} className="fw-finding">
          <span className="fw-finding-icon" aria-hidden="true"><TbAlertTriangle size={20} strokeWidth={1.8} /></span>
          <div>
            <strong>{W.findingWords(group.name, o)}</strong>
            <details className="fw-how is-quiet">
              <summary>{W.DETAILS}</summary>
              <p>{W.fleetPlaceWords(o.place, o.owner)}</p>
            </details>
          </div>
          <div className="fw-finding-go">
            <button type="button" className="fw-btn" onClick={() => go("groups")}>{W.reviewGroup(group.name)} <FiArrowRight size={14} aria-hidden="true" /></button>
            <button type="button" className="fw-btn is-ai" onClick={() => ask(o)}><AiIcon size={15} aria-hidden="true" /> Ask {ASSISTANT_NAME}</button>
          </div>
        </div>
      ))}
      <p className="fw-matrix-head">{odd.length ? W.matrixCaption(group.name) : head.text}</p>
      {places.length > 0 && (
        <div className="fw-matrix-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">{W.siteColumn}</th>
                {places.map((p) => {
                  const name = W.placeName(p);
                  return (
                    <th key={name} scope="col" className={lit.has(name) ? "is-odd" : undefined} title={p.owner || undefined}>
                      <strong>{W.orgName(p.owner) || p.net}</strong>
                      <span>{W.portWords(p)}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s} className={s === here ? "is-here" : undefined}>
                  <th scope="row">{s === here ? <><strong>{W.ACROSS.here}</strong><span>{s}</span></> : <strong>{s}</strong>}</th>
                  {places.map((p) => {
                    const name = W.placeName(p);
                    const at = p.sites.includes(s);
                    return (
                      <td key={name} className={lit.has(name) ? "is-odd" : undefined}>
                        <i className={`fw-dot${at ? "" : " is-none"}`} aria-label={at ? `${s} goes there` : `${s} does not`} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function FirewallAcross({ fleet, here, go }) {
  return (
    <section className="fw-card">
      <header className="fw-card-head">
        <h3 className="fw-card-title">{W.ACROSS.title}</h3>
        <details className="fw-how is-quiet">
          <summary>{W.HOW_IT_WORKS}</summary>
          <p>{W.ACROSS.means}</p>
        </details>
      </header>
      {fleet.groups.length === 0 && (
        <div className="fw-empty">
          <Sketch />
          <p className="fw-empty-lead">{W.ACROSS.none}</p>
          <ol className="fw-steps">
            {W.ACROSS.steps.map((s, i) => <li key={s}><span>{i + 1}</span><p>{s}</p></li>)}
          </ol>
        </div>
      )}
      {fleet.groups.map((g) => <Matrix key={g.name} group={g} odd={fleet.odd.filter((o) => o.group === g.name)} minPeers={fleet.minPeers} here={here} go={go} />)}
    </section>
  );
}
